/**
 * Swimlane row computation for the timeline UI.
 *
 * Transforms a TimelineSpan's children into SwimlaneRow[] for rendering
 * as horizontal swimlane bars. Handles sequential, iterative (multiple spans),
 * and parallel (overlapping) span patterns.
 */

import { createBranchSpan, type TimelineSpan } from "./core";

// =============================================================================
// Sorting
// =============================================================================

/** Compare rows/entries by start time, with end time as tiebreaker. */
export function compareByTime(
  a: { startTime: Date; endTime: Date },
  b: { startTime: Date; endTime: Date }
): number {
  return (
    a.startTime.getTime() - b.startTime.getTime() ||
    a.endTime.getTime() - b.endTime.getTime()
  );
}

/** Compare TimelineSpans by start time, with end time as tiebreaker. */
function compareSpansByTime(a: TimelineSpan, b: TimelineSpan): number {
  return (
    a.startTime(false).getTime() - b.startTime(false).getTime() ||
    a.endTime(false).getTime() - b.endTime(false).getTime()
  );
}

// =============================================================================
// Types
// =============================================================================

export interface SingleSpan {
  agent: TimelineSpan;
}

export interface ParallelSpan {
  agents: TimelineSpan[];
}

export type RowSpan = SingleSpan | ParallelSpan;

export interface SwimlaneRow {
  /** Unique key encoding tree position (e.g. "transcript/build/test"). */
  key: string;
  name: string;
  /** Depth in the span tree. 0 = root, 1 = direct child, etc. */
  depth: number;
  spans: RowSpan[];
  totalTokens: number;
  startTime: Date;
  endTime: Date;
  /** True when this row represents a timeline branch. */
  branch?: boolean;
  /** For branch rows: the branchedFrom message ID (fork point identifier). */
  branchedFrom?: string;
}

// =============================================================================
// Type Guards
// =============================================================================

export function isSingleSpan(span: RowSpan): span is SingleSpan {
  return "agent" in span;
}

export function isParallelSpan(span: RowSpan): span is ParallelSpan {
  return "agents" in span;
}

/** Unwrap a RowSpan to a flat array of TimelineSpan agents. */
export function getAgents(span: RowSpan): TimelineSpan[] {
  return isSingleSpan(span) ? [span.agent] : span.agents;
}

// =============================================================================
// Overlap Detection
// =============================================================================

/** Tolerance in milliseconds for considering two spans as overlapping. */
const OVERLAP_TOLERANCE_MS = 100;

// =============================================================================
// Main Computation
// =============================================================================

/**
 * Computes swimlane rows from a TimelineSpan's children.
 *
 * @returns Array of SwimlaneRow, with the parent row always first,
 *          followed by child rows ordered by earliest start time.
 */
export function computeSwimlaneRows(
  node: TimelineSpan,
  options?: { includeUtility?: boolean }
): SwimlaneRow[] {
  const includeUtility = options?.includeUtility ?? false;

  // Parent row is always first
  const parentRow = buildParentRow(node);

  // Collect child spans, optionally filtering utility agents
  const children = node.content.filter(
    (item): item is TimelineSpan =>
      item.type === "span" && (includeUtility || !item.utility)
  );

  if (children.length === 0) {
    return [parentRow];
  }

  // Group by name (case-insensitive)
  const groups = groupByName(children);

  // Build rows from groups
  const childRows: SwimlaneRow[] = [];
  for (const [displayName, spans] of groups) {
    const row = buildRowFromGroup(displayName, spans);
    if (row) {
      childRows.push(row);
    }
  }

  // Sort child rows by earliest start time
  childRows.sort(compareByTime);

  return [parentRow, ...childRows];
}

// =============================================================================
// Internal Helpers
// =============================================================================

/**
 * Assigns time-sorted spans to lanes using greedy bin-packing.
 *
 * Each lane contains non-overlapping spans. The number of lanes equals
 * the maximum parallelism level (minimum possible).
 * Input must be sorted by start time.
 */
export function assignToLanes(sorted: TimelineSpan[]): TimelineSpan[][] {
  if (sorted.length === 0) return [];

  const lanes: { spans: TimelineSpan[]; endTime: number }[] = [];

  for (const span of sorted) {
    const spanStart = span.startTime().getTime();
    let assigned = false;
    for (const lane of lanes) {
      if (lane.endTime + OVERLAP_TOLERANCE_MS <= spanStart) {
        lane.spans.push(span);
        lane.endTime = span.endTime().getTime();
        assigned = true;
        break;
      }
    }
    if (!assigned) {
      lanes.push({
        spans: [span],
        endTime: span.endTime().getTime(),
      });
    }
  }

  return lanes.map((l) => l.spans);
}

/**
 * Partitions time-sorted spans into clusters of overlapping spans.
 *
 * Uses a sweep-line: extends the current cluster while spans overlap its
 * time range, then starts a new cluster when a gap is found.
 * Input must be sorted by start time.
 */
function partitionIntoClusters(sorted: TimelineSpan[]): TimelineSpan[][] {
  if (sorted.length === 0) return [];

  const clusters: TimelineSpan[][] = [];
  let current: TimelineSpan[] = [sorted[0]!];
  let clusterEnd = sorted[0]!.endTime().getTime();

  for (let i = 1; i < sorted.length; i++) {
    const span = sorted[i]!;
    if (span.startTime().getTime() < clusterEnd + OVERLAP_TOLERANCE_MS) {
      // Overlaps with current cluster
      current.push(span);
      clusterEnd = Math.max(clusterEnd, span.endTime().getTime());
    } else {
      // Gap found — finalize current cluster and start a new one
      clusters.push(current);
      current = [span];
      clusterEnd = span.endTime().getTime();
    }
  }
  clusters.push(current);

  return clusters;
}

function buildParentRow(node: TimelineSpan): SwimlaneRow {
  return {
    key: node.name.toLowerCase(),
    name: node.name,
    depth: 0,
    spans: [{ agent: node }],
    totalTokens: node.totalTokens(false),
    startTime: node.startTime(false),
    endTime: node.endTime(false),
  };
}

/**
 * Groups spans by name (case-insensitive), preserving the display name
 * from the first span encountered in each group.
 *
 * Returns entries in insertion order (first-seen order).
 */
function groupByName(spans: TimelineSpan[]): [string, TimelineSpan[]][] {
  const map = new Map<string, { displayName: string; spans: TimelineSpan[] }>();

  for (const span of spans) {
    const key = span.name.toLowerCase();
    const existing = map.get(key);
    if (existing) {
      existing.spans.push(span);
    } else {
      map.set(key, { displayName: span.name, spans: [span] });
    }
  }

  return Array.from(map.values()).map((g) => [g.displayName, g.spans]);
}

function buildRowFromGroup(
  displayName: string,
  spans: TimelineSpan[],
  depth = 1,
  parentKey = ""
): SwimlaneRow | null {
  // Sort spans by start time, end time as tiebreaker
  const sorted = [...spans].sort(compareSpansByTime);

  const first = sorted[0];
  if (!first) {
    return null;
  }

  // Partition into overlapping clusters, then build RowSpans
  const rowSpans = partitionIntoClusters(sorted).map((cluster): RowSpan =>
    cluster.length === 1 ? { agent: cluster[0]! } : { agents: cluster }
  );

  // Compute aggregated time range and tokens
  const startTime = first.startTime(false);
  const endTime = sorted.reduce(
    (latest, span) =>
      span.endTime(false).getTime() > latest.getTime()
        ? span.endTime(false)
        : latest,
    first.endTime(false)
  );
  const totalTokens = sorted.reduce(
    (sum, span) => sum + span.totalTokens(false),
    0
  );

  const key = parentKey
    ? `${parentKey}/${displayName.toLowerCase()}`
    : displayName.toLowerCase();

  return {
    key,
    name: displayName,
    depth,
    spans: rowSpans,
    totalTokens,
    startTime,
    endTime,
  };
}

// =============================================================================
// Flat (Fully Expanded) Computation
// =============================================================================

/**
 * Computes a fully expanded flat list of swimlane rows from the entire span tree.
 *
 * Unlike `computeSwimlaneRows` (which only shows direct children), this function
 * recursively walks all descendant spans in depth-first pre-order. Each row carries
 * a `depth` for indentation and a unique `key` for selection.
 *
 * Same-name non-overlapping spans (iterative) are collapsed onto a single row
 * with multiple bars. Same-name overlapping spans (parallel) are expanded into
 * separate numbered rows.
 */
export function computeFlatSwimlaneRows(
  root: TimelineSpan,
  options?: { includeUtility?: boolean; showBranches?: boolean }
): SwimlaneRow[] {
  const includeUtility = options?.includeUtility ?? false;
  const showBranches = options?.showBranches ?? false;
  const parentRow = buildParentRow(root);
  const childRows = flattenChildren(
    [root],
    0,
    root.name.toLowerCase(),
    includeUtility,
    showBranches
  );
  return [parentRow, ...childRows];
}

/**
 * An entry produced by expanding a name group. Each entry becomes one row.
 * For iterative groups, there's a single entry with all spans merged.
 * For parallel groups, each span gets its own entry with a numbered name.
 */
interface FlatEntry {
  displayName: string;
  key: string;
  /** All TimelineSpans that contribute to this row's bars. */
  spans: TimelineSpan[];
  /** Pre-built RowSpans for this row (one SingleSpan per iterative span). */
  rowSpans: RowSpan[];
  totalTokens: number;
  startTime: Date;
  endTime: Date;
  /** Earliest start time across the entire name group this entry belongs to.
   *  Used for sorting so that all entries from the same agent name stay
   *  together (grouped) rather than being interleaved by individual start
   *  time with entries from other agent names. */
  groupStartTime: Date;
  /** Lane index within a parallel group (0-based). Preserves the greedy
   *  bin-packing order from `assignToLanes` so that numbered entries like
   *  "Explore 1" always sort before "Explore 2". -1 for non-parallel. */
  laneIndex: number;
}

/**
 * Recursively flattens descendant spans into rows in depth-first pre-order.
 *
 * Accepts multiple parent nodes so that iterative spans at the same level
 * can have their children merged before recursing.
 *
 * For each name group, partitions into overlapping clusters:
 * - If no cluster has >1 span (all iterative/sequential) → one row, multiple bars
 * - If any cluster has >1 span (parallel) → lanes via bin-packing, reusing lanes
 *   for non-overlapping spans (number of lanes = max parallelism level)
 */
function flattenChildren(
  nodes: TimelineSpan[],
  parentDepth: number,
  parentKey: string,
  includeUtility: boolean,
  showBranches: boolean,
  branchPrefix: string = ""
): SwimlaneRow[] {
  // Collect child spans, optionally filtering utility agents
  const children: TimelineSpan[] = [];
  for (const node of nodes) {
    for (const item of node.content) {
      if (item.type === "span" && (includeUtility || !item.utility)) {
        children.push(item);
      }
    }
  }
  if (children.length === 0 && !showBranches) return [];

  const groups = groupByName(children);
  const depth = parentDepth + 1;

  // Expand each group into one or more FlatEntries
  const entries: FlatEntry[] = [];

  for (const [displayName, spans] of groups) {
    const sorted = [...spans].sort(compareSpansByTime);
    const baseName = displayName.toLowerCase();
    const clusters = partitionIntoClusters(sorted);
    const hasParallel = clusters.some((c) => c.length > 1);
    // Earliest start across the whole name group — used for sorting so
    // that all entries from the same agent name stay together.
    const groupStartTime = sorted[0]!.startTime(false);

    if (!hasParallel) {
      // All non-overlapping (iterative): one row with one bar per cluster
      const rowSpans: RowSpan[] = sorted.map((s) => ({ agent: s }));
      const first = sorted[0]!;
      const endTime = sorted.reduce(
        (latest, s) =>
          s.endTime(false).getTime() > latest.getTime()
            ? s.endTime(false)
            : latest,
        first.endTime(false)
      );
      entries.push({
        displayName,
        key: `${parentKey}/${baseName}`,
        spans: sorted,
        rowSpans,
        totalTokens: sorted.reduce((sum, s) => sum + s.totalTokens(false), 0),
        startTime: first.startTime(false),
        endTime,
        groupStartTime,
        laneIndex: -1,
      });
    } else {
      // Has overlapping spans: assign to lanes via bin-packing.
      // Each lane becomes one row with multiple non-overlapping bars.
      const lanes = assignToLanes(sorted);

      if (lanes.length === 1) {
        // All spans fit in one lane — treat as iterative (no number suffix)
        const laneSpans = lanes[0]!;
        const rowSpans: RowSpan[] = laneSpans.map((s) => ({ agent: s }));
        const first = laneSpans[0]!;
        const endTime = laneSpans.reduce(
          (latest, s) =>
            s.endTime(false).getTime() > latest.getTime()
              ? s.endTime(false)
              : latest,
          first.endTime(false)
        );
        entries.push({
          displayName,
          key: `${parentKey}/${baseName}`,
          spans: laneSpans,
          rowSpans,
          totalTokens: laneSpans.reduce(
            (sum, s) => sum + s.totalTokens(false),
            0
          ),
          startTime: first.startTime(false),
          endTime,
          groupStartTime,
          laneIndex: -1,
        });
      } else {
        // Multiple lanes needed: one numbered row per lane
        for (let i = 0; i < lanes.length; i++) {
          const laneSpans = lanes[i]!;
          const rowSpans: RowSpan[] = laneSpans.map((s) => ({ agent: s }));
          const first = laneSpans[0]!;
          const endTime = laneSpans.reduce(
            (latest, s) =>
              s.endTime(false).getTime() > latest.getTime()
                ? s.endTime(false)
                : latest,
            first.endTime(false)
          );
          entries.push({
            displayName: `${displayName} ${i + 1}`,
            key: `${parentKey}/${baseName}-${i + 1}`,
            spans: laneSpans,
            rowSpans,
            totalTokens: laneSpans.reduce(
              (sum, s) => sum + s.totalTokens(false),
              0
            ),
            startTime: first.startTime(false),
            endTime,
            groupStartTime,
            laneIndex: i,
          });
        }
      }
    }
  }

  // Sort entries by group start time (keeps same-name entries together),
  // then by lane index (preserves "Explore 1" before "Explore 2"),
  // then by individual start time for non-parallel entries.
  entries.sort(
    (a, b) =>
      a.groupStartTime.getTime() - b.groupStartTime.getTime() ||
      a.laneIndex - b.laneIndex ||
      compareByTime(a, b)
  );

  // Emit rows: branch rows first (they fork from the parent node's execution),
  // then child span entries with recursive descent.
  const result: SwimlaneRow[] = [];

  // Emit branch rows when enabled. Each branch becomes a child row of the
  // node that owns it, with its own bar and optional nested children.
  // Branches are kept in the order the producer listed them (creation order).
  if (showBranches) {
    for (const node of nodes) {
      for (let i = 0; i < node.branches.length; i++) {
        const branch = node.branches[i]!;
        const label = `${branchPrefix}${i + 1}`;
        const branchSpan = createBranchSpan(branch, label);
        const branchKey = `${parentKey}/branch-${branch.branchedFrom}-${i + 1}`;
        result.push({
          key: branchKey,
          name: branchSpan.name,
          depth,
          spans: [{ agent: branchSpan }],
          totalTokens: branchSpan.totalTokens(false),
          startTime: branchSpan.startTime(false),
          endTime: branchSpan.endTime(false),
          branch: true,
          branchedFrom: branch.branchedFrom ?? undefined,
        });
        // Recurse into branch content for nested agents.
        // Pass the current label as prefix so nested branches get
        // hierarchical names (e.g. "Branch 1.1", "Branch 1.2").
        result.push(
          ...flattenChildren(
            [branchSpan],
            depth,
            branchKey,
            includeUtility,
            showBranches,
            `${label}.`
          )
        );
      }
    }
  }

  // Emit child span entries with recursive descent
  for (const entry of entries) {
    result.push({
      key: entry.key,
      name: entry.displayName,
      depth,
      spans: entry.rowSpans,
      totalTokens: entry.totalTokens,
      startTime: entry.startTime,
      endTime: entry.endTime,
    });
    result.push(
      ...flattenChildren(
        entry.spans,
        depth,
        entry.key,
        includeUtility,
        showBranches
      )
    );
  }

  return result;
}
